/** Scene module. Use SceneHandler only. Public: normalizeModel, createMesh, setCastReceiveShadows, createAtmosphericParticles, createVolumetricClouds, createDistantMountains. */

import * as THREE from "three";
import type { CreateMeshOptions } from "./types";

export type { CreateMeshOptions } from "./types";

export type NormalizeOptions = {
  normalizePosition?: boolean | THREE.Vector3 | [number, number, number] | number;
  normalizeGeometryScale?: boolean | number;
  centerGeometry?: boolean;
  normalizeRotation?: boolean;
  recursive?: boolean;
};

function toVec3(v: THREE.Vector3 | [number, number, number] | number | undefined): THREE.Vector3 | undefined {
  if (v === undefined) return undefined;
  if (typeof v === "number") return new THREE.Vector3(v, v, v);
  if (Array.isArray(v)) return new THREE.Vector3(v[0], v[1], v[2]);
  return v.clone();
}

export class SceneHandler {
  private scene?: THREE.Scene;

  constructor(scene?: THREE.Scene) {
    this.scene = scene;
  }

  /** Internal: normalize position/scale/rotation; optional center geometry, recursive. */
  private normalize(
    object: THREE.Object3D,
    options: NormalizeOptions = {}
  ): THREE.Object3D {
    const {
      normalizePosition = true,
      normalizeGeometryScale = true,
      centerGeometry = false,
      normalizeRotation = false,
      recursive = false,
    } = options;

    const normalizeObject = (obj: THREE.Object3D) => {
      if (normalizePosition !== false) {
        if (normalizePosition === true) {
          obj.position.set(0, 0, 0);
        } else {
          const pos = toVec3(normalizePosition);
          if (pos) obj.position.copy(pos);
        }
      }

      if (normalizeRotation) {
        obj.rotation.set(0, 0, 0);
        obj.quaternion.identity();
      }

      if (centerGeometry && (obj as THREE.Mesh).isMesh) {
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry) {
          if (!mesh.geometry.boundingBox) {
            mesh.geometry.computeBoundingBox();
          }
          const boundingBox = mesh.geometry.boundingBox!;
          const center = new THREE.Vector3();
          boundingBox.getCenter(center);
          mesh.geometry.translate(-center.x, -center.y, -center.z);
          mesh.geometry.computeBoundingBox();
        }
      }

      if (normalizeGeometryScale !== false && (obj as THREE.Mesh).isMesh) {
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry) {
          if (!mesh.geometry.boundingBox) {
            mesh.geometry.computeBoundingBox();
          }
          const boundingBox = mesh.geometry.boundingBox!;
          const size = new THREE.Vector3();
          boundingBox.getSize(size);

          // Use maximum dimension for normalization
          const maxDim = Math.max(size.x, size.y, size.z);
          if (maxDim > 0) {
            const targetSize = normalizeGeometryScale === true ? 1 : normalizeGeometryScale;
            const scaleFactor = targetSize / maxDim;
            mesh.geometry.scale(scaleFactor, scaleFactor, scaleFactor);
            mesh.geometry.computeBoundingBox();
          }
        }
      }

      obj.updateMatrix();
      obj.updateMatrixWorld(true);
    };

    if (recursive) {
      object.traverse(normalizeObject);
    } else {
      normalizeObject(object);
    }

    return object;
  }

  private isRiggedModel(model: THREE.Object3D): boolean {
    let hasRigging = false;
    model.traverse((child) => {
      if (child instanceof THREE.SkinnedMesh) {
        hasRigging = true;
      }
      // Also check for skeleton property
      if ((child as any).skeleton) {
        hasRigging = true;
      }
    });
    return hasRigging;
  }

  /** Normalize model; auto-detects rigged (Object3D scale) vs non-rigged (geometry). */
  normalizeModel<T extends THREE.Object3D>(
    model: T,
    options?: NormalizeOptions
  ): T {
    const config: NormalizeOptions = options || {};
    const isRigged = this.isRiggedModel(model);

    if (isRigged) {
      // For rigged models: DON'T scale/translate geometry (breaks bone relationships)
      // Only normalize position/rotation at Object3D level
      // Note: normalizeGeometryScale and centerGeometry are always false for rigged models
      this.normalize(model, {
        normalizeGeometryScale: false, // CRITICAL: Don't scale geometry (always false for rigged)
        centerGeometry: false,          // CRITICAL: Don't center geometry (always false for rigged)
        normalizePosition: config.normalizePosition !== undefined ? config.normalizePosition : true,
        normalizeRotation: config.normalizeRotation !== undefined ? config.normalizeRotation : true,
        recursive: false                 // Preserves skeleton structure (always false for rigged)
      });

      // Scale entire model (including skeleton) using Object3D scale
      // If normalizeGeometryScale is a number, use it as target size
      if (typeof config.normalizeGeometryScale === 'number') {
        // Ensure model matrix is updated before calculating bounding box
        model.updateMatrixWorld(true);
        // Use THREE.js built-in method to calculate bounding box
        const bbox = new THREE.Box3();
        bbox.setFromObject(model);
        const size = bbox.getSize(new THREE.Vector3());
        // Use maximum dimension for normalization
        const min = Math.min(size.x, size.y, size.z);
        if (min > 0) {
          const scaleFactor = config.normalizeGeometryScale / min;
          model.scale.setScalar(scaleFactor);
          // Update matrix after scaling to ensure bounding box calculations are correct
          model.updateMatrixWorld(true);
        }
      }
    } else {
      // For non-rigged models: Safe to normalize geometry directly
      // All options can be customized
      this.normalize(model, {
        normalizeGeometryScale: config.normalizeGeometryScale !== undefined ? config.normalizeGeometryScale : true,
        centerGeometry: config.centerGeometry !== undefined ? config.centerGeometry : true,
        normalizePosition: config.normalizePosition !== undefined ? config.normalizePosition : true,
        normalizeRotation: config.normalizeRotation !== undefined ? config.normalizeRotation : true,
        recursive: config.recursive !== undefined ? config.recursive : true
      });
    }

    return model;
  }

  /** Create mesh; optional normalize. Auto-adds to scene if set in constructor. */
  createMesh(
    geometry: THREE.BufferGeometry,
    material: THREE.Material | THREE.Material[],
    options: CreateMeshOptions & {
      normalize?: boolean | NormalizeOptions;
    } = {}
  ): THREE.Mesh {
    const mesh = new THREE.Mesh(geometry, material);
    if (options.name) mesh.name = options.name;
    if (options.castShadow !== undefined) mesh.castShadow = options.castShadow;
    if (options.receiveShadow !== undefined)
      mesh.receiveShadow = options.receiveShadow;
    if (options.frustumCulled !== undefined)
      mesh.frustumCulled = options.frustumCulled;
    if (options.renderOrder !== undefined)
      mesh.renderOrder = options.renderOrder;

    if (options.normalize !== false) {
      const normalizeOpts = options.normalize === true || options.normalize === undefined ? {} : options.normalize;
      this.normalizeModel(mesh, normalizeOpts);
    }

    if (this.scene) {
      this.scene.add(mesh);
    }

    return mesh;
  }

  /** Set cast/receive shadow flags (optional recursive). */
  setCastReceiveShadows(
    object: THREE.Object3D,
    cast: boolean,
    receive: boolean,
    recursive = true
  ): void {
    const fn = (o: THREE.Object3D) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.castShadow = cast;
        m.receiveShadow = receive;
      }
    };
    if (recursive) object.traverse(fn);
    else fn(object);
  }

  /** Create atmospheric Points particle system (count, radius, height, size, opacity, color). */
  createAtmosphericParticles(options: {
    count?: number;
    minRadius?: number;
    maxRadius?: number;
    minHeight?: number;
    size?: number;
    opacity?: number;
    colorHue?: number;
    colorSaturation?: number;
  } = {}): THREE.Points {
    const {
      count = 500,
      minRadius = 80,
      maxRadius = 230,
      minHeight = 30,
      size = 1.5,
      opacity = 0.6,
      colorHue = 0.55,
      colorSaturation = 0.2
    } = options;

    const particleGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      const radius = minRadius + Math.random() * (maxRadius - minRadius);
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;

      positions[i3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i3 + 1] = Math.abs(radius * Math.cos(phi) * 0.5) + minHeight;
      positions[i3 + 2] = radius * Math.sin(phi) * Math.sin(theta);

      const color = new THREE.Color();
      color.setHSL(
        colorHue + Math.random() * 0.1,
        colorSaturation,
        0.85 + Math.random() * 0.15
      );
      colors[i3] = color.r;
      colors[i3 + 1] = color.g;
      colors[i3 + 2] = color.b;
    }

    particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const particleMaterial = new THREE.PointsMaterial({
      size,
      vertexColors: true,
      transparent: true,
      opacity,
      sizeAttenuation: true,
      blending: THREE.AdditiveBlending,
    });

    const particleSystem = new THREE.Points(particleGeometry, particleMaterial);
    particleSystem.name = 'atmospheric-particles';

    if (this.scene) {
      this.scene.add(particleSystem);
    }

    return particleSystem;
  }

  /** Create volumetric cloud groups (count, height, spread, spheres, size, opacity). */
  createVolumetricClouds(options: {
    count?: number;
    minHeight?: number;
    maxHeight?: number;
    spread?: number;
    minSpheres?: number;
    maxSpheres?: number;
    minSize?: number;
    maxSize?: number;
    opacity?: number;
  } = {}): THREE.Group[] {
    const {
      count = 25,
      minHeight = 40,
      maxHeight = 140,
      spread = 200,
      minSpheres = 3,
      maxSpheres = 6,
      minSize = 6,
      maxSize = 16,
      opacity = 0.2
    } = options;

    const cloudGroups: THREE.Group[] = [];

    for (let i = 0; i < count; i++) {
      const cloudGroup = new THREE.Group();
      const sphereCount = minSpheres + Math.floor(Math.random() * (maxSpheres - minSpheres));

      for (let j = 0; j < sphereCount; j++) {
        const size = minSize + Math.random() * (maxSize - minSize);
        const cloudGeometry = new THREE.SphereGeometry(size, 8, 8);
        const cloudMaterial = new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: opacity + Math.random() * 0.15,
          fog: true,
          depthWrite: false,
        });

        const cloudSphere = new THREE.Mesh(cloudGeometry, cloudMaterial);
        cloudSphere.position.set(
          (Math.random() - 0.5) * 15,
          (Math.random() - 0.5) * 8,
          (Math.random() - 0.5) * 15
        );
        cloudGroup.add(cloudSphere);
      }

      cloudGroup.position.set(
        (Math.random() - 0.5) * spread,
        minHeight + Math.random() * (maxHeight - minHeight),
        (Math.random() - 0.5) * spread
      );

      cloudGroup.name = `cloud-group-${i}`;
      cloudGroups.push(cloudGroup);

      if (this.scene) {
        this.scene.add(cloudGroup);
      }
    }

    return cloudGroups;
  }

  /** Create distant mountain meshes (count, distance, height, radius, colorHue). */
  createDistantMountains(options: {
    count?: number;
    minDistance?: number;
    maxDistance?: number;
    minHeight?: number;
    maxHeight?: number;
    minRadius?: number;
    maxRadius?: number;
    colorHue?: number;
  } = {}): THREE.Mesh[] {
    const {
      count = 8,
      minDistance = 180,
      maxDistance = 230,
      minHeight = 30,
      maxHeight = 70,
      minRadius = 15,
      maxRadius = 35,
      colorHue = 0.55
    } = options;

    const mountains: THREE.Mesh[] = [];

    for (let i = 0; i < count; i++) {
      const height = minHeight + Math.random() * (maxHeight - minHeight);
      const radius = minRadius + Math.random() * (maxRadius - minRadius);

      const mountainGeometry = new THREE.ConeGeometry(radius, height, 5);
      const mountainMaterial = new THREE.MeshStandardMaterial({
        color: new THREE.Color().setHSL(colorHue, 0.3, 0.4 + Math.random() * 0.2),
        roughness: 0.9,
        metalness: 0.1,
        fog: true,
      });

      const mountain = new THREE.Mesh(mountainGeometry, mountainMaterial);
      const angle = (i / count) * Math.PI * 2;
      const distance = minDistance + Math.random() * (maxDistance - minDistance);

      mountain.position.set(
        Math.cos(angle) * distance,
        -5 + height / 2,
        Math.sin(angle) * distance
      );
      mountain.rotation.y = Math.random() * Math.PI;
      mountain.name = `mountain-${i}`;
      mountains.push(mountain);

      if (this.scene) {
        this.scene.add(mountain);
      }
    }

    return mountains;
  }
}

export default SceneHandler;
